import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../contexts/AppContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import { 
  Tooltip, 
  TooltipContent, 
  TooltipProvider, 
  TooltipTrigger 
} from '../components/ui/tooltip';
import { 
  CalendarIcon, 
  Save, 
  ArrowLeft, 
  Plus, 
  Calculator,
  X,
  HelpCircle
} from 'lucide-react';

export function AddTransaction() {
  const { addTransaction, getAccounts, getCategories } = useApp();
  const navigate = useNavigate();
  
  // Form state
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [amount, setAmount] = useState('');
  const [payee, setPayee] = useState('');
  const [accountId, setAccountId] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [memo, setMemo] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');
  const [cleared, setCleared] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  
  const accounts = getAccounts();
  const categories = getCategories();

  const addTag = () => {
    if (tagInput.trim() && !tags.includes(tagInput.trim())) {
      setTags([...tags, tagInput.trim()]);
      setTagInput('');
    }
  };

  const removeTag = (tagToRemove: string) => {
    setTags(tags.filter(tag => tag !== tagToRemove));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!amount || !payee || !accountId || !categoryId) {
      alert('Please fill in all required fields');
      return;
    }

    setIsLoading(true);

    try {
      addTransaction({
        date,
        accountId,
        amount: parseFloat(amount),
        payee: payee.trim(),
        categoryId,
        memo: memo.trim() || undefined,
        tags: tags.length > 0 ? tags : undefined,
        cleared,
        reconciled: false
      });

      // Reset form or navigate back
      if (window.confirm('Transaction added! Add another?')) {
        // Reset form but keep account and date
        setAmount('');
        setPayee('');
        setCategoryId('');
        setMemo('');
        setTags([]);
        setCleared(false);
      } else {
        navigate('/');
      }
    } catch (error) {
      console.error('Error adding transaction:', error);
      alert('Failed to add transaction');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="py-6 max-w-2xl mx-auto">
      <div className="flex items-center mb-6">
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={() => navigate('/')}
          className="mr-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <h1 className="text-2xl font-bold">Add Transaction</h1>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Transaction Details</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6" data-testid="add-transaction-form">
            {/* Date and Amount Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="date">Date *</Label>
                <div className="relative">
                  <Input
                    id="date"
                    type="date"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    data-testid="transaction-date"
                    required
                  />
                  <CalendarIcon className="absolute right-3 top-2.5 h-4 w-4 text-muted-foreground pointer-events-none" />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="amount">Amount *</Label>
                <div className="flex space-x-2">
                  <Input
                    id="amount"
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    data-testid="transaction-amount"
                    required
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    data-testid="calculator-button"
                    title="Quick Math"
                  >
                    <Calculator className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>

            {/* Payee */}
            <div className="space-y-2">
              <Label htmlFor="payee">Payee *</Label>
              <Input
                id="payee"
                type="text"
                placeholder="e.g., Grocery Store, John Smith, Salary"
                value={payee}
                onChange={(e) => setPayee(e.target.value)}
                data-testid="transaction-payee"
                required
              />
            </div>

            {/* Account and Category */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="account">Account *</Label>
                <Select value={accountId} onValueChange={setAccountId} required>
                  <SelectTrigger data-testid="account-select">
                    <SelectValue placeholder="Select account" />
                  </SelectTrigger>
                  <SelectContent>
                    {accounts.map((account) => (
                      <SelectItem key={account.id} value={account.id}>
                        {account.name} ({account.type})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="category">Category *</Label>
                <Select value={categoryId} onValueChange={setCategoryId} required>
                  <SelectTrigger data-testid="category-select">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((category) => (
                      <SelectItem key={category.id} value={category.id}>
                        {category.name}
                        {category.group && (
                          <span className="text-muted-foreground ml-2">({category.group})</span>
                        )}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Memo */}
            <div className="space-y-2">
              <Label htmlFor="memo">Memo</Label>
              <Textarea
                id="memo"
                placeholder="Additional notes about this transaction"
                value={memo}
                onChange={(e) => setMemo(e.target.value)}
                rows={2}
                data-testid="transaction-memo"
              />
            </div>

            {/* Tags */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Label htmlFor="tags">Tags</Label>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <HelpCircle className="w-4 h-4 text-muted-foreground cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent side="right" className="max-w-xs">
                      <div className="space-y-2">
                        <p className="font-medium">What are Tags?</p>
                        <p className="text-sm">
                          Add custom labels to organize your transactions beyond categories. 
                        </p>
                        <div className="text-xs space-y-1">
                          <p><strong>Examples:</strong></p>
                          <p>• "tax-deductible" for business expenses</p>
                          <p>• "vacation" for trip-related costs</p>
                          <p>• "gift" for presents and donations</p>
                          <p>• "monthly-bill" for recurring payments</p>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Press Enter or click Add to create tags. Click X to remove.
                        </p>
                      </div>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              
              {tags.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-2">
                  {tags.map((tag) => (
                    <Badge key={tag} variant="secondary" className="flex items-center gap-1 hover:bg-secondary/80 transition-colors">
                      {tag}
                      <X 
                        className="w-3 h-3 cursor-pointer hover:text-destructive transition-colors" 
                        onClick={() => removeTag(tag)}
                      />
                    </Badge>
                  ))}
                </div>
              )}
              
              <div className="flex space-x-2">
                <Input
                  placeholder="e.g., tax-deductible, vacation, gift"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addTag();
                    }
                  }}
                  className="flex-1"
                />
                <Button type="button" variant="outline" onClick={addTag} disabled={!tagInput.trim()}>
                  <Plus className="w-4 h-4 mr-1" />
                  Add
                </Button>
              </div>
            </div>

            {/* Cleared */}
            <div className="flex items-center space-x-2">
              <Checkbox
                id="cleared"
                checked={cleared}
                onCheckedChange={(checked) => setCleared(checked as boolean)}
                data-testid="transaction-cleared"
              />
              <Label htmlFor="cleared">Mark as cleared</Label>
            </div>

            {/* Submit Button */}
            <div className="flex space-x-2 pt-4">
              <Button type="submit" className="flex-1" disabled={isLoading} data-testid="save-transaction">
                {isLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin mr-2" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save Transaction
                  </>
                )}
              </Button>
              <Button type="button" variant="outline" onClick={() => navigate('/')}>
                Cancel
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}